import { useState, useMemo } from 'react';
import { Header, Footer } from '@shared/components';
import { SummaryCard } from '@features/news-summary';
import { NewspaperEffect } from '@shared/components';
import { CategoryTabs, useCategory, useCategories } from '@features/category-navigation';
import { SubCategoryTabs } from '@features/perspective-selection';
import { useSummary } from '@features/news-summary';
import type { SummaryData, SubCategorySummary } from '@features/news-summary';
import './HomePage.css';

export function HomePage() {
  const { categories } = useCategories();
  const { activeCategory, setActiveCategory } = useCategory();
  const {
    summaryResponse,
    loading,
    error,
    refetch,
    selectedSubCategory,
    setSelectedSubCategory,
  } = useSummary(activeCategory);

  const [showContrast, setShowContrast] = useState(false);

  // Find the active category's subcategory definitions from the categories API
  const activeSubCategories = useMemo(() => {
    const cat = categories.find(c => c.slug === activeCategory);
    return cat?.subCategories ?? [];
  }, [categories, activeCategory]);

  const getCurrentSummaryData = (): SummaryData | SubCategorySummary | null => {
    if (!summaryResponse) return null;

    if (summaryResponse.hasSubCategories && summaryResponse.subCategorySummaries) {
      const selected = summaryResponse.subCategorySummaries.find(
        s => s.subCategory.slug === selectedSubCategory
      );
      return selected || summaryResponse.subCategorySummaries[0] || null;
    }

    return summaryResponse.summary;
  };

  const currentSummary = getCurrentSummaryData();

  const handleContrastToggle = () => {
    setShowContrast(!showContrast);
  };

  const handleSubCategoryChange = (slug: string) => {
    setSelectedSubCategory(slug);
    setShowContrast(false);
  };

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setShowContrast(false);
  };

  return (
    <div className="home-page">
      <Header />

      <main id="main-content" className="home-page__main">
        <CategoryTabs
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
        />

        <div className="container">
          {activeSubCategories.length > 0 && (
            <SubCategoryTabs
              subCategories={activeSubCategories}
              selectedSubCategory={selectedSubCategory}
              onSubCategoryChange={handleSubCategoryChange}
              showContrastButton={summaryResponse?.enableContrasting === true && !!summaryResponse?.contrast}
              onContrastClick={handleContrastToggle}
              isContrastActive={showContrast}
            />
          )}

          <div className="home-page__content">
            {loading && (
              <div className="home-page__loading">
                <div className="home-page__loading-spinner" />
                <p>Loading today's news summary...</p>
              </div>
            )}

            {error && (
              <div className="home-page__error-container">
                <NewspaperEffect className="home-page__error">
                  <h3 className="home-page__error-headline">News Delivery Interrupted</h3>
                  <p className="home-page__error-text">{error}</p>
                  <button
                    className="home-page__retry-button"
                    onClick={refetch}
                    type="button"
                  >
                    Try Again
                  </button>
                </NewspaperEffect>
              </div>
            )}

            {summaryResponse && !loading && !error && (
              <div
                id={`category-panel-${activeCategory}`}
                role="tabpanel"
                aria-labelledby={`category-tab-${activeCategory}`}
              >
                {currentSummary ? (
                  <SummaryCard
                    summaryData={currentSummary}
                    categoryName={summaryResponse.categoryName}
                    date={summaryResponse.date}
                    edition={summaryResponse.edition}
                    generatedAt={summaryResponse.generatedAt}
                    contrast={summaryResponse.contrast}
                    showContrast={showContrast}
                  />
                ) : (
                  <div className="home-page__no-content">
                    <NewspaperEffect>
                      <p>No summary available for this category yet.</p>
                    </NewspaperEffect>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
